import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Mutex } from "async-mutex";
import { IMqttMessage } from "ngx-mqtt";
import { Observable, Subscription } from "rxjs";
import { debounceTime, filter, map, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppReserved } from "src/model/class/general classes/app-reserved";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { CustomerStaff } from "src/model/class/general classes/customer-staff";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { GenericOperationKind } from "src/model/enum/enumGenericOperationKind";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { CustomerStaffInfo } from "src/model/interface/customer-staff-info";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import { EventService } from "./event.service";
import { LoggerService } from "./logger.service";
import { MqttClientService } from "./mqtt-client.service";

@Injectable({
  providedIn: "root",
})
export class CustomerStaffService implements iEvent, iMqttHandling {
  private _logContext: string = "Customer-staff.service";

  /**Flag property; if the service ha foud an error in the last API call needed for get the customer-staff-list from the server, it is set to TRUE;
   * otherwise, it is set to FALSE.
   */
  private _isErrorInGetCustomerStaffListAPIRequest: boolean = false;

  /**
   * Flag property; store the ID of the customer/staff entity which has been modified by this application AND is currently being displayed; this
   * is used in order to filter out reaction to change on the mqtt address "RESERVED_DB_CUSTOMERSTAFF_DATAUPDATED" IF the update has been caused
   * by this application in the first place.
   */
  private _locallyModifiedCustomerID: number = -1;

  get isErrorInGetCustomerStaffListAPIRequest(): boolean {
    return this._isErrorInGetCustomerStaffListAPIRequest;
  }

  /**
   * Public setter of "_locallyModifiedCustomerID".
   */
  set locallyModifiedCustomerID(idCustomer: number) {
    this._locallyModifiedCustomerID = idCustomer;
  }

  _eventSubscriptions: Array<Subscription> = null;
  _mqttAddressSubscriptionsMap: Map<string, Subscription> = null;

  public countCustomerStaff: number = 0;
  public idCustomersStaffIfJustOne = -1;
  public needToInitialize: boolean = true;
  private _mutex = new Mutex();
  //private logoutSubscription: Subscription = null;

  constructor(
    private _eventService: EventService,
    private _http: HttpClient,
    private _mqttService: MqttClientService,
    private _loggerService: LoggerService
  ) {
    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();


    this.subscribeMQTTAddresses();
  }

  /**
   * Destroy event service
   */
  ngOnDestroy() {
    this.unsubscribeEvents();
    this.unsubscribeToMQTTAddresses();
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnLoginPlantDid.subscribe(() => {
        this.subscribeMQTTAddresses();
      })
    );

    /** Operation performed on logout */
    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {
        await this.unsubscribeToMQTTAddresses();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  //#region Mqtt addresses handling

  /**
   * Subscribtion from address service
   */
  async subscribeMQTTAddresses(): Promise<void> {
    let mqttAddressSubcription: Subscription = null;
    let returnedObservable: Observable<IMqttMessage> = null;

    //#region Subscribing to RESERVED_DB_CUSTOMERSTAFF_DATAUPDATED
    const mqttAddressCustomerStaffUpdated: string =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_DB_CUSTOMERSTAFF_DATAUPDATED;

    returnedObservable = await this._mqttService.subscribeTopic(
      mqttAddressCustomerStaffUpdated
    );

    mqttAddressSubcription = returnedObservable
      .pipe(
        map((mqttMessage: IMqttMessage) => {
          return this._mqttService.parseMessage(mqttMessage);
        }),
        filter(
          (message: Array<string>) =>
            (message?.[2] || "") !== this._mqttService.clientID
        ),
        map(async (message: Array<string>) => {
          if (message.length === 3) {
            const release = await this._mutex.acquire();

            try {
              // return message[1];
              return message;
            } finally {
              release();
            }
          } else {
            throw new Error(
              "OnCustomerStaffChanged - Incorrect format message"
            );
          }
        }),
        debounceTime(1000)
      )
      .subscribe(
        (message) => {
          message.then((message) => {
            const operatiopnKind = message[0];
            const idCustomerStaffChanged = message[1];

            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_mqttService.subscribeTopic",
              "AppReserved.RESERVED_DB_CUSTOMERSTAFF_DATAUPDATED",
              "NEW idCustomerStaffChanged: " + idCustomerStaffChanged
            );

            switch (+operatiopnKind) {
              case GenericOperationKind.CUSTOMERSTAFF_REMOVE:
                this._eventService.OnCustomerStaffDeleted.next(
                  idCustomerStaffChanged
                );
                break;

              case GenericOperationKind.CUSTOMERSTAFF_CHANGE:
              default:
                this._eventService.OnCustomerStaffChanged.next(
                  idCustomerStaffChanged
                );
                break;
            }
          });
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_mqttService.subscribeTopic",
            "AppReserved.RESERVED_DB_CUSTOMERSTAFF_DATAUPDATED",
            "Incorect message format: " + error.message
          );
        }
      );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddressCustomerStaffUpdated,
      mqttAddressSubcription
    );

    //#endregion
  }

  async checkLocalAddressSubscriptionMap(
    //
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "Address ALREADY present " + addressToCheck
      );
    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "NEW address: " + addressToCheck
      );
      result = true;
    }
    return result;
  }

  async unsubscribeToMQTTAddresses(): Promise<void> {
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      entry[1].unsubscribe();

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }

  async unsubscribeToSingleMQTTAddress(address: string): Promise<void> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      "_eventService",
      "unsubscribeToSingleMQTTAddress",
      "LOCAL MAP-SUBSCRIPTIONS 1: "
    );
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        "_eventService",
        "unsubscribeToSingleMQTTAddress",
        "LOCAL MAP-SUBSCRIPTIONS 1 - ENTRY: " + entry[0]
      );
    }

    if (this._mqttAddressSubscriptionsMap.has(address)) {
      const subToObservable = this._mqttAddressSubscriptionsMap.get(address);
      subToObservable.unsubscribe();
      this._mqttAddressSubscriptionsMap.delete(address);
    }

    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        "_eventService",
        "unsubscribeToSingleMQTTAddress",
        "LOCAL MAP-SUBSCRIPTIONS 2 - ENTRY: " + entry[0]
      );
    }
  }

  //#endregion

  /**
   * Get  list of customer, filtred by a peculiar queryString
   *
   * @param queryString Query string for detailed search
   * @returns
   */
  public async getCustomerByQueryString(
    queryString: string = ""
  ): Promise<Array<CustomerStaff>> {

    if (queryString.length > 0) {
      queryString = "?" + queryString;
    }
    return this._http
      .get(
        environment.httpServerRoot + AppSettings.API_URL_CUSTOMERS + queryString
      )
      .pipe(
        map((customerStaffStore: GenericApiData) => customerStaffStore.data)
      )
      .toPromise();
  }

  /**
   * Get customer data from API by ID
   *
   * @param idCustomerStaff ID customer staff to serarch
   * @returns
   */
  public async getCustomerStaffbyID(
    idCustomerStaff: number
  ): Promise<CustomerStaff> {
    try {
      const listCustomerStaff: Array<CustomerStaff> =
        await this.getCustomerByQueryString(
          "idCustomerStaff=" + idCustomerStaff
        );

      if (listCustomerStaff?.length > 0) {
        return listCustomerStaff?.[0];
      } else {
        throw new Error();
      }
    } catch {
      throw new Error();
    }
  }

  /**
   * Assign customerStaff to Group
   *
   * @param idCustomerStaff
   * @param idGroup
   */
  public async assignCustomerToGroup(idCustomerStaff, idGroup) {
    return this._http
      .patch(
        environment.httpServerRoot +
        AppSettings.API_URL_CUSTOMERS +
        "/" +
        idCustomerStaff +
        "/assign/group/" +
        idGroup,
        ""
      )
      .toPromise()
      .then(
        (result: any) => {
          return result;
        },
        (error) => {
          throw new Error();
        }
      );
  }

  /**
   * Assign customerStaff to Group
   *
   * @param idCustomerStaff
   * @param idGroup
   */
  public async diassignCustomerToGroup(idCustomerStaff) {
    return this._http
      .delete(
        environment.httpServerRoot +
        AppSettings.API_URL_CUSTOMERS +
        "/" +
        idCustomerStaff +
        "/disassign/group/"
      )
      .toPromise()
      .then(
        (result: any) => {
          return result;
        },
        (error) => {
          throw new Error();
        }
      );
  }

  /**
   * Insert new customer via API
   *
   * @param insertCustomerStaff Customer staff data object
   */
  public async insertCustomerStaff(insertCustomerStaff: CustomerStaff) {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "insertCustomer()",
      "insertCustomerStaff: " + JSON.stringify(insertCustomerStaff)
    );

    return this._http
      .post<CustomerStaff>(
        environment.httpServerRoot + AppSettings.API_URL_CUSTOMERS,
        insertCustomerStaff
      )
      .pipe(
        tap((insertCustomerStaff: CustomerStaff) => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_CUSTOMERSTAFF_DATAUPDATED;
          const mqttMessage =
            GenericOperationKind.CUSTOMERSTAFF_CHANGE.toString() +
            "|" +
            insertCustomerStaff.id.toString();

          this._mqttService.publishAddress(mqttAddress, mqttMessage);
        })
      )
      .toPromise();
  }

  /**
   * Update existing customer
   *
   * @param idCustomerStaff id customer staff update
   * @param customerStaffData customer staff updating
   */
  public async updateCustomer(
    idCustomerStaff: number,
    customerStaffData: CustomerStaff
  ): Promise<CustomerStaff> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "updateCustomer()",
      "Customer staff to update: " + JSON.stringify(customerStaffData)
    );

    return this._http
      .put(
        environment.httpServerRoot +
        AppSettings.API_URL_CUSTOMERS +
        "/" +
        idCustomerStaff,
        customerStaffData
      )
      .pipe(
        tap((updateCustomerStaff: CustomerStaff) => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_CUSTOMERSTAFF_DATAUPDATED;
          const mqttMessage =
            GenericOperationKind.CUSTOMERSTAFF_CHANGE.toString() +
            "|" +
            updateCustomerStaff.id.toString();

          this._mqttService.publishAddress(mqttAddress, mqttMessage);
        })
      )
      .toPromise();
  }

  /**
   * Update plate on customer
   *
   * @param idCustomerStaff id customer staff update
   * @param customerStaffData customer staff updating
   */
  public async updatePlateCustomer(
    idCustomerStaff: number,
    plate1: string,
    plate2: string,
    plate3: string,
    plate4: string
  ): Promise<any> {
    const bodyRequest = {
      Plate1: plate1,
      Plate2: plate2,
      Plate3: plate3,
      Plate4: plate4,
    };
    return this._http
      .patch(
        environment.httpServerRoot +
        AppSettings.API_URL_CUSTOMERS +
        "/" +
        idCustomerStaff +
        "/update/plates",
        bodyRequest
      )
      .pipe(
        tap((updated) => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_CUSTOMERSTAFF_DATAUPDATED;
          const mqttMessage =
            GenericOperationKind.CUSTOMERSTAFF_CHANGE.toString() +
            "|" +
            idCustomerStaff.toString();

          this._mqttService.publishAddress(mqttAddress, mqttMessage);
        })
      )
      .toPromise();
  }

  /**
   * Delete existing customer
   *
   * @param idCustomerStaffToDelete ID customer staff to delete
   */
  public deleteCustomer(idCustomerStaffToDelete: number): Promise<any> {
    return this._http
      .delete(
        environment.httpServerRoot +
        AppSettings.API_URL_CUSTOMERS +
        "/" +
        idCustomerStaffToDelete
      )
      .pipe(
        tap((cancel) => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_CUSTOMERSTAFF_DATAUPDATED;
          const mqttMessage =
            GenericOperationKind.CUSTOMERSTAFF_REMOVE.toString() +
            "|" +
            idCustomerStaffToDelete.toString();

          /** Fire propagation to all connected client */

          this._mqttService.publishAddress(mqttAddress, mqttMessage);
        })
      )
      .toPromise();
  }

  /**
   * Returns the info regarding the customerStaff with id equal to idCustomerStaff
   * @param idCustomerStaff
   * @returns
   */
  public getCustomerStaffInfo(
    idCustomerStaff: number
  ): Promise<CustomerStaffInfo> {
    return this._http
      .get<CustomerStaffInfo>(
        environment.httpServerRoot +
        AppSettings.API_URL_CUSTOMERS +
        "/" +
        idCustomerStaff +
        "/info"
      )
      .toPromise();
  }

  /**
   * Assign photo to customer staff
   * @param idCustomerStaff id current customer staff to assign photo
   * @param idPhoto id photo staff to assign to customer staff
   * @param formatPhoto format (.png, .jpg) to assign user photo
   */
  public changeCustomerStaffPhoto(
    idCustomerStaff: number,
    idPhoto: number,
    formatPhoto: string = "png"
  ) {
    const bodyRequest = {
      id: idPhoto,
    };

    return this._http
      .patch(
        environment.httpServerRoot +
        AppSettings.API_URL_CUSTOMERS +
        "/" +
        idCustomerStaff +
        "/photo",
        bodyRequest
      )
      .pipe(
        tap(() => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_CUSTOMERSTAFF_DATAUPDATED;
          const mqttMessage =
            GenericOperationKind.CUSTOMERSTAFF_CHANGE.toString() +
            "|" +
            idCustomerStaff.toString();


          this._mqttService.publishAddress(mqttAddress, mqttMessage);

          this._eventService.OnPropagatePhotoChanged.next(idCustomerStaff);

          //this._eventService.OnCustomerStaffChangedLocally.next(idCustomerStaff)
        })
      )
      .toPromise();
  }
}
